import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/hooks/useAuth';

interface UserMenuProps {
  showAccountLabel?: boolean; // Show "Account" text instead of avatar (for mobile menu)
  onItemSelect?: () => void; // Called when an account item is selected (e.g. to close a containing mobile sheet)
}

export function UserMenu({ showAccountLabel = false, onItemSelect }: UserMenuProps) {
  const { isAuthenticated, user, signOut, isLoading, isDevMode } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const isAdmin = useMemo(() => {
    return user?.roles?.includes('admin') ?? false;
  }, [user?.roles]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return <div className="h-10 w-20 animate-pulse bg-muted rounded-md" />;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/auth/login">
          <Button variant="ghost" size="sm">
            Sign in
          </Button>
        </Link>
        <Link to="/auth/signup">
          <Button size="sm">Sign up</Button>
        </Link>
      </div>
    );
  }

  const handleItemSelect = () => {
    setIsOpen(false);
    onItemSelect?.();
  };

  const handleSignOut = async () => {
    handleItemSelect();
    await signOut();
    navigate('/auth/login');
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-sm hover:bg-accent transition-colors"
      >
        {showAccountLabel ? (
          <span className="text-foreground">Account</span>
        ) : (
          <>
            <span className="h-6 w-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-medium">
              {user?.username?.charAt(0).toUpperCase() ||
                user?.email?.charAt(0).toUpperCase() ||
                '?'}
            </span>
            <span className="hidden sm:inline max-w-[150px] truncate text-foreground">
              {user?.username || user?.email || 'User'}
            </span>
          </>
        )}
        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md bg-card border border-border shadow-lg z-50">
          <div className="p-3 border-b border-border">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.username || user?.email}
            </p>
            {user?.roles && user.roles.length > 0 && (
              <div className="flex gap-1 mt-1 flex-wrap">
                {user.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary"
                  >
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="p-1">
            <Link
              to="/contributor/dashboard"
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              Arche Dashboard
            </Link>
            <Link
              to="/credits"
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              Credits
            </Link>
            {isAdmin && (
              <Link
                to="/dev-console"
                className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
                onClick={handleItemSelect}
              >
                Dev Console
              </Link>
            )}
            <Link
              to="/my-data"
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              My Data
            </Link>
            <Link
              to="/workspaces"
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              Workspace Controls
            </Link>
            <Link
              to="/auth/settings"
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              Account Settings
            </Link>
            <Link
              to="/a/arche_customer_support"
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
              onClick={handleItemSelect}
            >
              Support
            </Link>
            {isDevMode && (
              <>
                <div className="border-t border-border my-1" />
                <Link
                  to="/auth/switch-user"
                  className="block w-full px-3 py-2 text-sm text-left text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 rounded-md"
                  onClick={handleItemSelect}
                >
                  Switch Dev User
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={handleSignOut}
              className="block w-full px-3 py-2 text-sm text-left text-foreground hover:bg-accent rounded-md"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
